/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import React, {
  FC,
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react';
import {withTranslation} from 'react-i18next';
import {useDispatch, useSelector} from 'react-redux';
import {Page} from '@ion/applications/src/types/pages';
import Navigation, {SideNavProps} from '@ion/dls/src/components/navigation/Navigation';
import {useMedia} from '@ion/dls/src/hooks/useMedia';
import {SideNavAdminIcon} from '@ion/dls/src/icons/SideNavAdminIcon';
import {SideNavAnalyticsIcon} from '@ion/dls/src/icons/SideNavAnalyticsIcon';
import {SideNavDashboardIcon} from '@ion/dls/src/icons/SideNavDashboardIcon';
import {SideNavDevicesIcon} from '@ion/dls/src/icons/SideNavDevicesIcon';
import {SideNavManufacturingIcon} from '@ion/dls/src/icons/SideNavManufacturingIcon';
import {SideNavNotificationsIcon} from '@ion/dls/src/icons/SideNavNotificationsIcon';
import {SideNavProvisioningIcon} from '@ion/dls/src/icons/SideNavProvisioningIcon';
import {SideNavReportsIcon} from '@ion/dls/src/icons/SideNavReportsIcon';
import {SideNavSensorsIcon} from '@ion/dls/src/icons/SideNavSensorsIcon';
import {SideNavServicesIcon} from '@ion/dls/src/icons/SideNavServicesIcon';
import {SideNavSystemConfigIcon} from '@ion/dls/src/icons/SideNavSystemConfigIcon';
import {SideNavToolBoxIcon} from '@ion/dls/src/icons/SideNavToolBoxIcon';
import {SideNavVehicleEngineeringServicesIcon} from '@ion/dls/src/icons/SideNavVehicleEngineeringServicesIcon';
import {dlsColor} from '@ion/dls/src/theme/theme';
import {MenuItems} from '@ion/dls/src/types/components';
import changePage, {
  getRouteDefinition,
  getRouteLabel,
  Route
} from '@/routes/RouteDefinitions';
import {
  setActiveIndex,
  setSideSheetStatus,
  toggleSecondaryNav
} from '@/store/actions/nav';
import {authorizedPages} from '@/store/reducers/nav';
import {
  activeIndexSelector,
  navSelectionSelector,
  navSidesheetSelector
} from '@/store/selectors/navigation';
import SideSheetContent from './SideSheetContent';
import styles from './SideNav.module.scss';

/**
 * Provides the mapping of ION page navigation items to their respective
 * DLS Icons.
 */
const pageIconMap = {
  dashboard                    : SideNavDashboardIcon,
  provisioning                 : SideNavProvisioningIcon,
  admin                        : SideNavAdminIcon,
  systemconfig                 : SideNavSystemConfigIcon,
  reports                      : SideNavReportsIcon,
  [Route.ANALYTICS]            : SideNavReportsIcon,
  [Route.ANALYTICS_DASHBOARDS] : SideNavAnalyticsIcon,
  devices                      : SideNavDevicesIcon,
  notifications                : SideNavNotificationsIcon,
  toolbox                      : SideNavToolBoxIcon,
  manufacturing                : SideNavManufacturingIcon,
  vehicle                      : SideNavVehicleEngineeringServicesIcon,
  sensors                      : SideNavSensorsIcon,
  services                     : SideNavServicesIcon
};

/**
 * @setIcon
 *
 * Returns the icon component for the side nav with the correct
 * coloring based on the current selected page.
 */
const setIcon = (page: string, currentPage: string) => {
  const selected = currentPage?.includes(page);
  const Icon = pageIconMap[page];
  if (Icon) {
    return (<Icon color={selected ? dlsColor('grey2') : dlsColor('white')} />);
  }
  return null;
};

/**
 * @getPages
 *
 * Given the pages state from the nav reducer, returns a list of menu items
 * based on the pages to which the user is authorized to access.
 */
export const getPages = (pages: Page[], currentPage: string) => {
  const result = authorizedPages(pages).map((page: Page) => {
    const {route} = page;
    const entry = {
      page  : `/${route}`,
      label : getRouteLabel(route),
      icon  : setIcon(route, currentPage),
      ...page
    };
    if (page.pages) {
      entry.pages = getPages(page.pages as Page[], currentPage);
    }
    return entry;
  });
  return result;
};

// we only initialize the routeChangeComplete event once for the life of the session ...
// let routerInitialized = false;

/**
 * @SideNav
 *
 * The SideNav component provides the CTC Admin Application Side Navigation UI.
 */
export const SideNav: FC = ({onToggleCollapse}: SideNavProps) => {
  const {
    pages,
    sideSheet: sideSheetStatus,
    isUserProfileView
  }                            = useSelector(navSidesheetSelector);
  const {
    currentPage,
    secondaryNav,
    selectedIndex: index
  }                               = useSelector(navSelectionSelector);
  const activeIndex               = useSelector(activeIndexSelector);
  const dispatch                  = useDispatch();
  const {isDesktop}               = useMedia();
  const [menuItems, setMenuItems] = useState<MenuItems>([]);
  const defaultMenuItems          = useMemo(() => getPages(pages, currentPage), [pages]);

  useEffect(() => {
    setMenuItems(defaultMenuItems);
  }, [defaultMenuItems]);

  const toggleSideSheet = useCallback(function({page, desktopStatus = false, mobileStatus = false}) {
    dispatch(setSideSheetStatus({
      activeSideSheet      : page,
      desktopSideSheetOpen : desktopStatus,
      mobileSideSheetOpen  : mobileStatus
    }));
  }, []);

  const closeSecondaryNavigation = useCallback(function() {
    dispatch(toggleSecondaryNav(false, NaN));
    dispatch(setActiveIndex(-1));
  }, []);

  const getSideSheetContent = useCallback(function() {
    return <SideSheetContent toggleSideSheet={toggleSideSheet} />;
  }, [toggleSideSheet]);

  /**
   * Invoked when an item within the Navigation Side Bar is selected, at which
   * point the route is pushed to next Router if the route is not a secondary
   * navigation item, otherwise the secondary navigation's view state is set
   * to true.
   */
  const setPage = useCallback(function(page: string, index: number, isSecondaryIndex?: boolean) {
    const pageIndex = (secondaryNav?.show && (!isDesktop || isSecondaryIndex)) ? activeIndex : index;
    dispatch(setActiveIndex((isDesktop && pageIndex === activeIndex) ? -1 : pageIndex));
    switch (getRouteDefinition(page)) {
      case Route.PROVISIONING:
        dispatch(toggleSecondaryNav(!secondaryNav?.show, index));
        break;
      case Route.REPORTS:
        dispatch(toggleSecondaryNav(!secondaryNav?.show, index));
        break;
      case Route.ADMIN:
        dispatch(toggleSecondaryNav(!secondaryNav?.show, index));
        break;
      case Route.SYSTEM_CONFIG:
        dispatch(toggleSecondaryNav(!secondaryNav?.show, index));
        break;
      case Route.TOOLBOX:
        dispatch(toggleSecondaryNav(!secondaryNav?.show, index));
        break;
      case Route.MANUFACTURING:
        dispatch(toggleSecondaryNav(!secondaryNav?.show, index));
        break;
      case Route.VEHICLE:
        dispatch(toggleSecondaryNav(!secondaryNav?.show, index));
        break;
      case Route.SENSORS:
        dispatch(toggleSecondaryNav(!secondaryNav?.show, index));
        break;
      case Route.SERVICES:
        dispatch(toggleSecondaryNav(!secondaryNav?.show, index));
        break;
      default:
        isDesktop && dispatch(toggleSecondaryNav(false, index));
        changePage(page as Route, undefined);
    }
  }, [isDesktop, secondaryNav]);

  return (
    <div className={styles.container}>
      <Navigation useExpandCollapseFeature={isDesktop}
        onToggleCollapse={onToggleCollapse}
        selectedIndex={index}
        activeIndex={activeIndex}
        hasMultipleSubModule={true}
        isCtcAdmin
        onSetPage={setPage}
        menuItems={menuItems}
        secondaryNav={secondaryNav}
        getPages={getPages}
        closeSecondaryNavigation={closeSecondaryNavigation}
        currentPage={currentPage}
        sideSheetContent={getSideSheetContent}
        toggleSideSheet={toggleSideSheet}
        sideSheetStatus={sideSheetStatus}
        toolTipPosition={'right'}
        toolTipArrow
        isUserProfileView={isUserProfileView} />
    </div>
  );
};

export default withTranslation('nav')(React.memo(SideNav));
